import React, { useEffect, useState } from 'react';
import { Save, Image as ImageIcon, Upload, X, AlertCircle, Plus, Trash2, Home, FileText, Phone, LayoutTemplate } from 'lucide-react';
import { adminApi, formatApiError, resolveImageUrl } from '../../utils/adminApi';

// Schema describing every editable section + their fields.
// Field types: text, textarea, image, list (array of subforms)
const SECTIONS = [
  {
    key: 'home.hero',
    label: 'Home — Hero',
    icon: Home,
    fields: [
      { key: 'eyebrow', label: 'Eyebrow', type: 'text' },
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'subtitle', label: 'Subtitle', type: 'text' },
      { key: 'tagline', label: 'Tagline (use line breaks)', type: 'textarea' },
      { key: 'ctaText', label: 'Button text', type: 'text' },
      { key: 'image', label: 'Hero image', type: 'image' },
    ],
  },
  {
    key: 'home.heritage',
    label: 'Home — Heritage Pillars',
    icon: Home,
    fields: [
      { key: 'eyebrow', label: 'Eyebrow', type: 'text' },
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
      {
        key: 'cards', label: 'Cards (max 3 recommended)', type: 'list',
        item: [
          { key: 'subtitle', label: 'Eyebrow / Subtitle', type: 'text' },
          { key: 'title', label: 'Title', type: 'text' },
          { key: 'description', label: 'Description', type: 'textarea' },
          { key: 'image', label: 'Image', type: 'image' },
          { key: 'filter', label: 'Filter (search term linked to Archive — e.g. zardozi, aari, silk). Leave blank to show full archive.', type: 'text' },
        ],
      },
    ],
  },
  {
    key: 'home.collection',
    label: 'Home — Collection Section',
    icon: Home,
    fields: [
      { key: 'eyebrow', label: 'Eyebrow', type: 'text' },
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'viewAllText', label: 'Button text', type: 'text' },
    ],
  },
  {
    key: 'home.suits',
    label: 'Home — Suit Collection',
    icon: Home,
    fields: [
      { key: 'eyebrow', label: 'Eyebrow', type: 'text' },
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'viewAllText', label: 'Button text', type: 'text' },
      { key: 'emptyText', label: 'Empty-state message (shown when no suit products yet)', type: 'text' },
    ],
  },
  {
    key: 'home.readytowear',
    label: 'Home — Ready-to-Wear Section',
    icon: Home,
    fields: [
      { key: 'eyebrow', label: 'Eyebrow', type: 'text' },
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'viewAllText', label: 'Button text', type: 'text' },
    ],
  },
  {
    key: 'home.testimonials',
    label: 'Home — Testimonials',
    icon: Home,
    fields: [
      { key: 'eyebrow', label: 'Eyebrow', type: 'text' },
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
      {
        key: 'items', label: 'Testimonials', type: 'list',
        item: [
          { key: 'quote', label: 'Quote', type: 'textarea' },
          { key: 'name', label: 'Patron name', type: 'text' },
          { key: 'attribution', label: 'Attribution (e.g. Bridal Commission, Jaipur)', type: 'text' },
        ],
      },
    ],
  },
  {
    key: 'home.bespoke',
    label: 'Home — Bespoke Journey',
    icon: Home,
    fields: [
      { key: 'eyebrow', label: 'Eyebrow', type: 'text' },
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'appointmentText', label: 'Appointment label', type: 'text' },
      { key: 'ctaText', label: 'CTA button text', type: 'text' },
      {
        key: 'steps', label: 'Steps', type: 'list',
        item: [
          { key: 'title', label: 'Title', type: 'text' },
          { key: 'description', label: 'Description', type: 'text' },
          { key: 'icon', label: 'Icon (message | fabric | craft | delivery)', type: 'text' },
        ],
      },
    ],
  },
  {
    key: 'about',
    label: 'About Page',
    icon: FileText,
    fields: [
      { key: 'eyebrow', label: 'Eyebrow', type: 'text' },
      { key: 'heading', label: 'Heading', type: 'text' },
      { key: 'leadParagraph', label: 'Lead paragraph', type: 'textarea' },
      { key: 'philosophyHeading', label: 'Philosophy heading', type: 'text' },
      { key: 'philosophyText', label: 'Philosophy body (separate paragraphs with blank line)', type: 'textarea' },
      { key: 'philosophyQuote', label: 'Quote', type: 'textarea' },
      { key: 'essenceHeading', label: 'Essence section heading', type: 'text' },
      {
        key: 'essence', label: 'What We Stand For (numbered list)', type: 'list',
        item: [
          { key: 'number', label: 'Number (e.g. 01)', type: 'text' },
          { key: 'text', label: 'Text', type: 'textarea' },
        ],
      },
      { key: 'ctaHeading', label: 'CTA heading', type: 'text' },
      { key: 'ctaText', label: 'CTA body', type: 'textarea' },
      { key: 'ctaPrimary', label: 'CTA primary button', type: 'text' },
      { key: 'ctaSecondary', label: 'CTA secondary button', type: 'text' },
    ],
  },
  {
    key: 'contact',
    label: 'Contact Page',
    icon: Phone,
    fields: [
      { key: 'eyebrow', label: 'Eyebrow', type: 'text' },
      { key: 'heading', label: 'Heading', type: 'text' },
      { key: 'intro', label: 'Intro paragraph', type: 'textarea' },
      { key: 'email', label: 'Email', type: 'text' },
      { key: 'phone', label: 'Phone', type: 'text' },
      { key: 'whatsappNumber', label: 'WhatsApp number (digits only)', type: 'text' },
      { key: 'address', label: 'Address (use line breaks)', type: 'textarea' },
      { key: 'hours', label: 'Hours (use line breaks)', type: 'textarea' },
    ],
  },
  {
    key: 'footer',
    label: 'Footer',
    icon: LayoutTemplate,
    fields: [
      { key: 'tagline', label: 'Tagline', type: 'text' },
      { key: 'email', label: 'Email', type: 'text' },
      { key: 'phone', label: 'Phone', type: 'text' },
      { key: 'phoneHref', label: 'Phone link (tel:...)', type: 'text' },
      { key: 'address', label: 'Address (use line breaks)', type: 'textarea' },
      { key: 'copyright', label: 'Copyright', type: 'text' },
    ],
  },
];

const PagesTab = ({ onToast }) => {
  const [activeKey, setActiveKey] = useState(SECTIONS[0].key);
  const [allContent, setAllContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.getAllContent();
      setAllContent(data || {});
    } catch (err) {
      setError(formatApiError(err, 'Could not load content.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const section = SECTIONS.find((s) => s.key === activeKey);

  return (
    <div data-testid="pages-tab" className="admin-pages-shell">
      <aside className="admin-pages-sidebar" data-testid="pages-sidebar">
        {SECTIONS.map((s) => {
          const Icon = s.icon || FileText;
          return (
            <button
              key={s.key}
              className={`admin-pages-nav ${activeKey === s.key ? 'active' : ''}`}
              onClick={() => setActiveKey(s.key)}
              data-testid={`pages-nav-${s.key}`}
            >
              <Icon size={14} />
              <span>{s.label}</span>
            </button>
          );
        })}
      </aside>
      <div className="admin-pages-main">
        {loading && <p className="body-regular">Loading…</p>}
        {error && <div className="admin-error"><AlertCircle size={14} /> {error}</div>}
        {!loading && section && (
          <SectionEditor
            section={section}
            initial={allContent[section.key] || {}}
            onSaved={(updated) => {
              setAllContent({ ...allContent, [section.key]: updated });
              onToast?.('Saved');
            }}
          />
        )}
      </div>
    </div>
  );
};

const SectionEditor = ({ section, initial, onSaved }) => {
  const [data, setData] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { setData(initial); }, [initial]);

  const setField = (key, value) => setData((d) => ({ ...d, [key]: value }));

  const save = async () => {
    setErr('');
    setSaving(true);
    try {
      const res = await adminApi.updateContent(section.key, data);
      onSaved?.(res.data || data);
    } catch (e) {
      setErr(formatApiError(e, 'Save failed.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-pages-editor" data-testid={`pages-editor-${section.key}`}>
      <header className="admin-pages-header">
        <div>
          <p className="navigation-text" style={{ color: 'var(--accent-gold)' }}>Editing</p>
          <h2 className="heading-2" style={{ marginTop: 4 }}>{section.label}</h2>
        </div>
        <button className="btn-primary" onClick={save} disabled={saving} data-testid="pages-save-btn">
          <Save size={14} /> {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </header>

      <div className="admin-pages-fields">
        {section.fields.map((field) => (
          <FieldRenderer
            key={field.key}
            field={field}
            value={data[field.key]}
            onChange={(v) => setField(field.key, v)}
          />
        ))}
      </div>

      {err && <div className="admin-error" style={{ marginTop: 16 }}><AlertCircle size={14} /> {err}</div>}
    </div>
  );
};

const FieldRenderer = ({ field, value, onChange }) => {
  if (field.type === 'text') {
    return (
      <label className="admin-field">
        <span className="admin-field-label">{field.label}</span>
        <input
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`field-${field.key}`}
        />
      </label>
    );
  }
  if (field.type === 'textarea') {
    return (
      <label className="admin-field">
        <span className="admin-field-label">{field.label}</span>
        <textarea
          rows={4}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`field-${field.key}`}
        />
      </label>
    );
  }
  if (field.type === 'image') {
    return <ImageField field={field} value={value} onChange={onChange} />;
  }
  if (field.type === 'list') {
    return <ListField field={field} value={value} onChange={onChange} />;
  }
  return null;
};

const ImageField = ({ field, value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr('');
    setUploading(true);
    try {
      const { url } = await adminApi.uploadImage(file);
      onChange(url);
    } catch (e2) {
      setErr(formatApiError(e2, 'Upload failed.'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="admin-field admin-field-image">
      <span className="admin-field-label">{field.label}</span>
      <div className="admin-image-uploader" style={{ marginBottom: 0 }}>
        {value ? (
          <div className="admin-image-preview">
            <img src={resolveImageUrl(value)} alt="preview" />
            <button type="button" className="admin-image-remove" onClick={() => onChange('')}>
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="admin-image-empty"><ImageIcon size={32} /></div>
        )}
        <label className="admin-upload-btn">
          <Upload size={14} /> {uploading ? 'Uploading…' : (value ? 'Replace' : 'Upload')}
          <input type="file" accept="image/jpeg,image/png" onChange={handleFile} hidden data-testid={`field-${field.key}-file`} />
        </label>
        <input
          type="text"
          placeholder="…or paste image URL"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="admin-image-url"
          data-testid={`field-${field.key}-url`}
        />
        {err && <div className="admin-error"><AlertCircle size={14} /> {err}</div>}
      </div>
    </div>
  );
};

const ListField = ({ field, value, onChange }) => {
  const items = Array.isArray(value) ? value : [];

  const updateItem = (idx, key, val) => {
    const next = [...items];
    next[idx] = { ...(next[idx] || {}), [key]: val };
    onChange(next);
  };

  const removeItem = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    onChange(next);
  };

  const addItem = () => {
    const empty = {};
    field.item.forEach((sub) => { empty[sub.key] = ''; });
    onChange([...items, empty]);
  };

  return (
    <div className="admin-field admin-field-list">
      <span className="admin-field-label">{field.label}</span>
      <div className="admin-list-items">
        {items.map((item, idx) => (
          <div key={idx} className="admin-list-item" data-testid={`list-${field.key}-${idx}`}>
            <div className="admin-list-item-head">
              <strong>#{idx + 1}</strong>
              <button type="button" className="admin-icon-only" onClick={() => removeItem(idx)} data-testid={`list-${field.key}-${idx}-remove`}>
                <Trash2 size={14} />
              </button>
            </div>
            <div className="admin-list-item-body">
              {field.item.map((sub) => (
                <FieldRenderer
                  key={sub.key}
                  field={{ ...sub, key: `${field.key}-${idx}-${sub.key}`, label: sub.label }}
                  value={item[sub.key]}
                  onChange={(v) => updateItem(idx, sub.key, v)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <button type="button" className="btn-secondary" onClick={addItem} data-testid={`list-${field.key}-add`}>
        <Plus size={14} /> Add item
      </button>
    </div>
  );
};

export default PagesTab;
